import { z } from "zod";

import { createSlice } from "@reduxjs/toolkit";

import { stateOptions } from "../utils/stateOptions";
import { PhoneValidation } from "../utils/phoneValidation";

const nameRegex = new RegExp(/^[A-Za-z '-]*$/);
const streetRegex = new RegExp(/^[A-Za-z0-9 ,.'&-]*$/);
const stateValues = stateOptions.map((s) => s.value) as [string, ...string[]];

export const profileSchema = z.object({
  userId: z.string().uuid().or(z.literal("")).optional(),
  profile: z.object({
    firstName: z.string().min(1, "First name is required").regex(nameRegex, {
      message: "Invalid character used.",
    }),
    lastName: z.string().min(1, "Last name is required").regex(nameRegex, {
      message: "Invalid character used.",
    }),
    phone: z.string().min(1, "Phone number is required").refine(
      (val) => {
        return PhoneValidation.isValid(val);
      },
      { message: "Invalid phone number format. Please enter a valid international phone number." },
    ),
    orgName: z.string().min(1, "Organization name is required"),
    subOrgName: z.string().min(1, "Department is required").refine((val) => nameRegex.test(val), {
      message: "Invalid character used.",
    }),
    subSubOrgName: z.string().min(1, "Team is required").refine((val) => nameRegex.test(val), {
      message: "Invalid character used.",
    }),
  }),
});

export type ProfileType = z.infer<typeof profileSchema>;

const initialState: ProfileType = {
  userId: undefined,
  profile: {
    firstName: "",
    lastName: "",
    phone: "",
    orgName: "",
    subOrgName: "",
    subSubOrgName: "",
  },
};

export const profileSlice = createSlice({
  name: "profile",
  initialState,
  reducers: {
    loadData: (state, action) => {
      const payload = action.payload;
      if (payload.user_id) {
        state.userId = payload.user_id;
        state.profile.firstName = payload.first_name;
        state.profile.lastName = payload.last_name;
        state.profile.phone = payload.phone;
        state.profile.orgName = payload.org_name;
        state.profile.subOrgName = payload.sub_org_name;
        state.profile.subSubOrgName = payload.sub_sub_org_name;
      }
    },
    updateData: (state, action) => {
      state.userId = action.payload.userId;
      state.profile = { ...state.profile, ...action.payload.profile };
    },
  },
  extraReducers: (builder) => {
    builder.addCase("auth/clearAll", () => {
      return initialState;
    });
  },
});

export const { loadData, updateData } = profileSlice.actions;
export default profileSlice.reducer;
